import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Target, TriangleAlert } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";

const tone = (pct) => (pct >= 100 ? "text-emerald-600" : pct >= 80 ? "text-amber-600" : "text-rose-600");

/** Who is below their productive-hours target, and for how long. */
export function ProdAlertsPanel({ query }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/productivity/alerts?${query}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load targets"))
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <Card className="flex h-40 items-center justify-center p-4">
      <Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-500" />
    </Card>;
  }
  if (!data) return null;

  const flagged = data.alerting || [];
  const tracked = (data.people || []).filter((p) => p.workdays_tracked > 0);
  const untracked = (data.people || []).filter((p) => !p.workdays_tracked);

  return (
    <div className="space-y-4" data-testid="prod-alerts-panel">
      <Card className={`p-4 ${flagged.length ? "border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/30/60" : "border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30/60"}`}
        data-testid="prod-alerts-banner">
        <div className="flex items-start gap-3">
          {flagged.length
            ? <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            : <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />}
          <div>
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {flagged.length
                ? `${flagged.length} below target for ${data.consecutive_days}+ working days`
                : "Nobody is currently below target"}
            </p>
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
              A person is flagged after {data.consecutive_days} consecutive working days under{" "}
              {data.threshold_pct}% of their target. Change this in Settings → Targets &amp; alerts.
            </p>
            {flagged.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {flagged.map((p) => (
                  <span key={p.key} data-testid={`prod-alert-chip-${p.key}`}
                    className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-medium text-rose-800 dark:text-rose-200">
                    {p.name} · {p.below_run}d
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <Target className="h-4 w-4 text-slate-400 dark:text-slate-400" />
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Against target</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="prod-alerts-table">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-800 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-400">
                <th className="py-2 pr-3 font-semibold">Person</th>
                <th className="py-2 pr-3 font-semibold">Team</th>
                <th className="py-2 pr-3 font-semibold">Avg productive/day</th>
                <th className="py-2 pr-3 font-semibold">Target</th>
                <th className="py-2 pr-3 font-semibold">Of target</th>
                <th className="py-2 pr-3 font-semibold">Days hit</th>
                <th className="py-2 font-semibold">Below run</th>
              </tr>
            </thead>
            <tbody>
              {tracked.map((p) => (
                <tr key={p.key} data-testid={`prod-alerts-row-${p.key}`}
                  className={`border-b border-slate-50 ${p.alerting ? "bg-rose-50 dark:bg-rose-950/30/50" : ""}`}>
                  <td className="py-2 pr-3 font-medium text-slate-800 dark:text-slate-100">
                    {p.name}
                    {p.alerting && <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">flagged</span>}
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-500 dark:text-slate-400">{p.team}</td>
                  <td className="py-2 pr-3">{p.avg_productive_hms}</td>
                  <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">{p.goal_hms}</td>
                  <td className={`py-2 pr-3 font-semibold ${tone(p.goal_pct)}`}>{p.goal_pct}%</td>
                  <td className="py-2 pr-3 text-xs text-slate-500 dark:text-slate-400">{p.days_hit} of {p.workdays_tracked}</td>
                  <td className="py-2 text-xs">
                    {p.below_run
                      ? <span className="text-rose-700">{p.below_run} day{p.below_run === 1 ? "" : "s"}
                        <span className="ml-1 text-slate-400 dark:text-slate-400">({p.below_run_dates.slice(-3).join(", ")})</span></span>
                      : <span className="text-slate-400 dark:text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {untracked.length > 0 && (
          <p className="mt-3 text-xs text-slate-400 dark:text-slate-400" data-testid="prod-alerts-untracked">
            No activity recorded in this range for: {untracked.map((p) => p.name).join(", ")}. They aren&apos;t
            flagged — there&apos;s nothing to measure.
          </p>
        )}
      </Card>
    </div>
  );
}
